import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button,
  Paper,
  Container,
  Breadcrumbs,
  Link,
  Divider,
  Stack,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { DoctorAvailabilityForm, DoctorAvailability } from '../../components/employees/DoctorAvailabilityForm';
import { useGetEmployeeById, updateEmployeeAvailability } from '../../api/employees';

const DoctorAvailabilityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  
  // Fetch employee data
  const { data: employee, isLoading, error } = useGetEmployeeById(id || '');

  const [availability, setAvailability] = useState<DoctorAvailability>({
    useSameSchedule: true,
    weeklyAvailability: {
      monday: {
        isWorking: true,
        sessions: 1,
        morningSession: { startTime: '09:00', endTime: '13:00' },
        afternoonSession: { startTime: '14:00', endTime: '18:00' },
        fullDaySession: { startTime: '09:00', endTime: '18:00' }
      },
      tuesday: { isWorking: true, sessions: 1, morningSession: { startTime: '09:00', endTime: '13:00' }, afternoonSession: { startTime: '14:00', endTime: '18:00' }, fullDaySession: { startTime: '09:00', endTime: '18:00' } },
      wednesday: { isWorking: true, sessions: 1, morningSession: { startTime: '09:00', endTime: '13:00' }, afternoonSession: { startTime: '14:00', endTime: '18:00' }, fullDaySession: { startTime: '09:00', endTime: '18:00' } },
      thursday: { isWorking: true, sessions: 1, morningSession: { startTime: '09:00', endTime: '13:00' }, afternoonSession: { startTime: '14:00', endTime: '18:00' }, fullDaySession: { startTime: '09:00', endTime: '18:00' } },
      friday: { isWorking: true, sessions: 1, morningSession: { startTime: '09:00', endTime: '13:00' }, afternoonSession: { startTime: '14:00', endTime: '18:00' }, fullDaySession: { startTime: '09:00', endTime: '18:00' } },
      saturday: { isWorking: false, sessions: 1, morningSession: { startTime: '09:00', endTime: '13:00' }, afternoonSession: { startTime: '14:00', endTime: '18:00' }, fullDaySession: { startTime: '09:00', endTime: '18:00' } },
      sunday: { isWorking: false, sessions: 1, morningSession: { startTime: '09:00', endTime: '13:00' }, afternoonSession: { startTime: '14:00', endTime: '18:00' }, fullDaySession: { startTime: '09:00', endTime: '18:00' } },
    },
    defaultAvailability: {
      isWorking: true,
      sessions: 1,
      morningSession: { startTime: '09:00', endTime: '13:00' },
      afternoonSession: { startTime: '14:00', endTime: '18:00' },
      fullDaySession: { startTime: '09:00', endTime: '18:00' }
    }
  });

  const handleChange = (newValue: DoctorAvailability) => {
    setAvailability(newValue);
  };

  const handleSubmit = async (data: DoctorAvailability) => {
    if (!id) return;
    
    try {
      setIsSubmitting(true);
      await updateEmployeeAvailability(id, data);
      setSnackbar({
        open: true,
        message: 'Availability updated successfully!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating availability:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update availability. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !employee) {
    return (
      <Box p={3}>
        <Typography color="error">
          {error?.message || 'Employee not found'}
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to Employee
        </Button>
        
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" href="/settings/employees">
            Employees
          </Link>
          <Link
            underline="hover"
            color="inherit"
            href={`/settings/employees/${id}`}
          >
            {employee.firstName} {employee.lastName}
          </Link>
          <Typography color="text.primary">Availability</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Doctor Availability
          </Typography>
        </Stack>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {employee.firstName} {employee.lastName}'s Availability
        </Typography>
        <Typography color="text.secondary" paragraph>
          Configure working hours, breaks, and availability for appointments.
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        <DoctorAvailabilityForm
          value={availability}
          onChange={handleChange}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
        
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default DoctorAvailabilityPage;
